import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BE_API_URL } from '../constants/app.constants';

export type IntegrationProviderStatus = 'available' | 'coming_soon' | string;
export type IntegrationAuthType = 'manual_credentials' | 'oauth2' | 'api_key' | string;

export interface IntegrationCapability {
  key: string;            // 'properties' | 'contacts' | 'documents' | ...
  label: string;          // UI label
  labelKey?: string;
  description?: string;   // optional helper text
  descriptionKey?: string;
}

export interface IntegrationCredentialField {
  key: string;
  label: string;
  labelKey?: string;
  type: 'text' | 'password' | 'url';
  placeholder?: string;
  placeholderKey?: string;
  default?: string;
  required?: boolean;
  helpText?: string;
  helpTextKey?: string;
}

export interface IntegrationProviderCatalogItem {
  provider: string;                 // e.g. 'easimo'
  displayName: string;              // e.g. 'Easimo'
  description?: string;
  descriptionKey?: string;
  status: IntegrationProviderStatus;

  authType: IntegrationAuthType;    // manual_credentials | oauth2 | api_key
  logoUrl?: string;
  docsUrl?: string;
  helpUrl?: string;

  credentialSchema?: IntegrationCredentialField[];  // only for manual/api_key
  supportsScopes?: boolean;          // if it has userIds selection
  supportsScheduling?: boolean;      // if scheduling is meaningful

  capabilities?: IntegrationCapability[]; // modules/features supported by provider
  defaultEnabledCapabilities?: string[];  // keys that should be enabled by default
  setupMode?: 'sftp_manual' | string;
  customerVisible?: boolean;
}

export interface SftpIntegrationSetup {
  id: string;
  provider: 'sftp';
  connectionType: 'sftp_ingestion';
  lifecycleStatus: 'awaiting_provisioning' | 'awaiting_first_file' | 'active' | 'paused' | 'disconnected' | 'error';
  dataSource: 'haussoft_excel';
  publicKeyConfigured: boolean;
  sshKeyFingerprint: string;
  acceptedFileName: string;
  acceptedFileExtension: 'xlsx' | 'xls';
  lastReceivedAt?: string | null;
  lastImport?: null | {
    status: 'queued' | 'claimed' | 'parsing' | 'validating' | 'processing' | 'finalizing' | 'completed' | 'completed_with_errors' | 'failed' | string;
    finishedAt?: string | null;
  };
  revocationRequired: boolean;
  endpoint: null | {
    hostname: string;
    port: number;
    hostKeyFingerprint: string;
    username: string;
    uploadDirectory: string;
  };
}

export interface SftpImportHistoryItem {
  jobId: string;
  fileName: string;
  status: string;
  receivedAt?: string | null;
  createdAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  summary?: {
    totalRows?: number;
    createdCount?: number;
    updatedCount?: number;
    skippedCount?: number;
    failedCount?: number;
  };
  hasErrorReport: boolean;
  failureKind?: string;
  superseded?: boolean;
}

export type SftpImportHistorySortBy =
  | 'createdAt'
  | 'receivedAt'
  | 'finishedAt'
  | 'status'
  | 'fileName'
  | 'createdCount'
  | 'updatedCount'
  | 'skippedCount'
  | 'failedCount';

export type SftpImportHistorySortOrder = 'asc' | 'desc';

export interface SftpImportHistoryResponse {
  items: SftpImportHistoryItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  sortBy: SftpImportHistorySortBy;
  sortOrder: SftpImportHistorySortOrder;
}

export interface SftpNotificationSettings {
  recipientUserIds: string[];
  events: { success: boolean; issues: boolean; failure: boolean };
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface SftpNotificationRecipient {
  id: string;
  displayName: string;
  email: string;
  companyRole: string;
}

export interface IntegrationConnectionLastRunSummary {
  id?: string;
  status?: 'running' | 'success' | 'failed' | 'partial' | string;
  startedAt?: string;
  endedAt?: string;
  errorsCount?: number;
}

export interface IntegrationConnectionSettings {
  enabledCapabilities?: string[]; // modules selection
  scope?: { userIds?: string[] };

  // schedule/pause
  syncMode?: 'manual' | 'hourly' | 'daily' | 'weekly' | string;
  schedule?: {
    hour?: number;      // 0..23 for daily/weekly
    minute?: number;    // 0..59
    dayOfWeek?: number; // 1..7 (Mon..Sun) for weekly
    timezone?: string;  // optional
  };
  paused?: boolean;

  [key: string]: any;
}

export interface IntegrationConnection {
  id: string;
  provider: string;
  connectionType?: 'api' | 'sftp_ingestion' | string;
  sftpLifecycleStatus?: SftpIntegrationSetup['lifecycleStatus'] | null;
  sftpLastImportFinishedAt?: string | null;
  displayName?: string; // optional if BE includes it
  status: 'connected' | 'disconnected' | 'error' | string;
  lastSyncAt?: string;
  lastRun?: IntegrationConnectionLastRunSummary;
  settings?: IntegrationConnectionSettings;

  createdAt?: string;
  updatedAt?: string;
}

export interface IntegrationLimits {
  allowed: number;
  used: number;
}

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  constructor(private http: HttpClient) {}

  listProviders(): Observable<IntegrationProviderCatalogItem[]> {
    return this.http.get<IntegrationProviderCatalogItem[]>(`${BE_API_URL}/auth/integrations/providers`);
  }

  listConnections(): Observable<{ items: IntegrationConnection[]; limits?: IntegrationLimits }> {
    return this.http.get<{ items: IntegrationConnection[]; limits?: IntegrationLimits }>(`${BE_API_URL}/auth/integrations`);
  }

  getConnection(id: string): Observable<IntegrationConnection> {
    return this.http.get<IntegrationConnection>(`${BE_API_URL}/auth/integrations/${id}`);
  }

  // Upsert (1 connection per provider per company)
  createOrUpdateConnection(provider: string, credentials: Record<string, any>): Observable<{ id: string; status?: string }> {
    return this.http.post<{ id: string; status?: string }>(`${BE_API_URL}/auth/integrations`, { provider, credentials });
  }

  updateConnection(id: string, payload: { settings?: any; credentials?: any }): Observable<any> {
    return this.http.put(`${BE_API_URL}/auth/integrations/${id}`, payload);
  }

  deleteConnection(id: string): Observable<void> {
    return this.http.delete<void>(`${BE_API_URL}/auth/integrations/${id}`);
  }

  testConnection(id: string): Observable<{ ok: boolean; message?: string }> {
    return this.http.post<{ ok: boolean; message?: string }>(`${BE_API_URL}/auth/integrations/${id}/test`, {});
  }

  listScopes(id: string): Observable<{ userIds: Array<{ id: string; label: string }> }> {
    return this.http.get<{ userIds: Array<{ id: string; label: string }> }>(`${BE_API_URL}/auth/integrations/${id}/scopes`);
  }

  runSync(id: string): Observable<{ runId: string }> {
    return this.http.post<{ runId: string }>(`${BE_API_URL}/auth/integrations/${id}/sync`, {});
  }

  getStatus(id: string): Observable<any> {
    return this.http.get(`${BE_API_URL}/auth/integrations/${id}/status`);
  }

  getRuns(id: string): Observable<any> {
    return this.http.get(`${BE_API_URL}/auth/integrations/${id}/runs`);
  }

  getRunErrors(id: string, runId: string): Observable<any> {
    return this.http.get(`${BE_API_URL}/auth/integrations/${id}/runs/${runId}/errors`);
  }

  // OAuth flow (generic)
  startOAuth(provider: string, redirectUri: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${BE_API_URL}/auth/integrations/oauth/start`, { provider, redirectUri });
  }

  finishOAuth(provider: string, code: string, state: string, redirectUri: string): Observable<{ connectionId: string }> {
    return this.http.post<{ connectionId: string }>(`${BE_API_URL}/auth/integrations/oauth/callback`, {
      provider, code, state, redirectUri
    });
  }

  getSftpIntegration(): Observable<{ item: SftpIntegrationSetup | null }> {
    return this.http.get<{ item: SftpIntegrationSetup | null }>(`${BE_API_URL}/auth/integrations/sftp`);
  }

  requestSftpIntegration(sshPublicKey: string): Observable<{ item: SftpIntegrationSetup }> {
    return this.http.post<{ item: SftpIntegrationSetup }>(`${BE_API_URL}/auth/integrations/sftp/request`, { sshPublicKey });
  }

  replaceSftpPublicKey(id: string, sshPublicKey: string): Observable<{ item: SftpIntegrationSetup }> {
    return this.http.put<{ item: SftpIntegrationSetup }>(`${BE_API_URL}/auth/integrations/${id}/sftp/public-key`, { sshPublicKey });
  }

  changeSftpLifecycle(id: string, action: 'pause' | 'resume' | 'disconnect'): Observable<{ item: SftpIntegrationSetup }> {
    return this.http.post<{ item: SftpIntegrationSetup }>(`${BE_API_URL}/auth/integrations/${id}/sftp/${action}`, {});
  }

  getSftpImportHistory(
    id: string,
    page = 1,
    limit = 20,
    sortBy: SftpImportHistorySortBy = 'createdAt',
    sortOrder: SftpImportHistorySortOrder = 'desc'
  ): Observable<SftpImportHistoryResponse> {
    return this.http.get<SftpImportHistoryResponse>(
      `${BE_API_URL}/auth/integrations/${encodeURIComponent(id)}/sftp/import-history`,
      {
        params: {
          page: String(page),
          limit: String(limit),
          sortBy,
          sortOrder,
        },
      }
    );
  }

  getSftpNotificationSettings(id: string): Observable<{ settings: SftpNotificationSettings }> {
    return this.http.get<{ settings: SftpNotificationSettings }>(`${BE_API_URL}/auth/integrations/${encodeURIComponent(id)}/sftp/notification-settings`);
  }

  getSftpNotificationRecipients(id: string): Observable<{ items: SftpNotificationRecipient[] }> {
    return this.http.get<{ items: SftpNotificationRecipient[] }>(`${BE_API_URL}/auth/integrations/${encodeURIComponent(id)}/sftp/notification-recipients`);
  }

  updateSftpNotificationSettings(id: string, settings: SftpNotificationSettings): Observable<{ settings: SftpNotificationSettings }> {
    return this.http.put<{ settings: SftpNotificationSettings }>(`${BE_API_URL}/auth/integrations/${encodeURIComponent(id)}/sftp/notification-settings`, settings);
  }
}
